import { useState, useCallback, useEffect, useMemo } from 'react';
import { SelectPicker } from 'rsuite';
import searchIcon from '../../../assets/img/search-icon.svg';
import { Card } from '../../../components/card.component';
import { ExportButton } from '../../../components/export.component';
import { AppRouteApi } from '../../../config/appRoute';
import { useAppDispatch, useAppSelector } from '../../../core/redux/ReduxStore';
import { ActionsFinancePatientTable } from '../../../core/redux/slices/sliceFinanceTable';
import { PatientFinanceList } from '../../../features/finance/patientFinanceList.component';
import {
  IPaginationSearchFilter,
  useTablePaginationSearchFilter,
} from '../../../hooks';
import { ApiSchemaFilteredPatientFinance } from '../../../models/api';
import { ApiPatient, ApiCaregiver } from '../../../core/apis';
import { StorageAuth } from '../../../core/localStorage';

export const PageFinancePatient = () => {
  const dispatch = useAppDispatch();
  const { pageNumber, pageSize, searchKey, nextPageToken, shouldRefetch } =
    useAppSelector((state) => state.financePatientTable);

  const selectedCompany = StorageAuth.CompanyId || '';
  // State for filters
  const [selectedPatient, setSelectedPatient] = useState('');
  const [selectedCaregiver, setSelectedCaregiver] = useState('');
  const { data: patients } = ApiPatient.GetAllFromCompany.Hook(selectedCompany);
  const { data: caregivers } = ApiCaregiver.GetAllFromCompany.Hook(selectedCompany);

  const apiParams = useMemo<IPaginationSearchFilter['customParams']>(() => {
    return {
      NextPageToken: nextPageToken,
      PageSize: pageSize,
      PageNumber: pageNumber,
      SearchKey: searchKey,
      PatientSKey: selectedPatient || undefined,
      CaregiverSKey: selectedCaregiver || undefined,
    };
  }, [nextPageToken, pageSize, pageNumber, searchKey, selectedPatient, selectedCaregiver]);

  const {
    apiData: financeData,
    isFetching: isFetchingFinanceData,
    refetch: refetchFinanceData,
  } = useTablePaginationSearchFilter<
    ApiSchemaFilteredPatientFinance,
    typeof ActionsFinancePatientTable
  >(ActionsFinancePatientTable, {
    customParams: apiParams,
    apiRoute: AppRouteApi.Finance.PatientV2(),
  });

  const searchPatientList = useCallback(
    (ev: React.ChangeEvent<HTMLInputElement>): void => {
      dispatch(ActionsFinancePatientTable.goToPage(1));
      dispatch(ActionsFinancePatientTable.setSearchKey(ev.target.value));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    shouldRefetch && refetchFinanceData();
  }, [shouldRefetch, refetchFinanceData]);

  useEffect(() => {
    return () => {
      dispatch(ActionsFinancePatientTable.resetData());
    };
  }, [dispatch]);

  const patientsSelectItems = patients?.map((patient) => ({
    label: `${patient.firstName} ${patient.lastName}`,
    value: patient.sKey,
  })) || [];

  const caregiverSelectItems = useMemo(() => {
    return caregivers?.map((caregiver) => ({
      label: `${caregiver.firstName} ${caregiver.lastName}`,
      value: caregiver.sKey,
    })) || [];
  }, [caregivers]);

  return (
    <Card
      header={
        <div className='row align-items-center justify-content-between'>
          <div className='col-md-8 col-12 content-box-title'>
            <div>
              <p className='text-capitalize my-1 text-green-200'>
                Patients' Finance
              </p>
            </div>
            <div className='collapse-icon'></div>
          </div>
          <div className='col-md-4 col-12 mt-1'>
            <form className='search-component'>
              <div className='input-group me-3'>
                <span
                  className='input-group-text pe-2'
                  id='search-component-word'
                >
                  <img src={searchIcon} alt='search-icon' />
                </span>
                <input
                  type='text'
                  className='form-control ps-0'
                  placeholder='Search...'
                  aria-label='Search'
                  aria-describedby='search-component-word'
                  onChange={searchPatientList}
                  value={searchKey}
                  onKeyPress={(ev) => {
                    ev.key === 'Enter' && ev.preventDefault();
                  }}
                />
                {searchKey.length > 0 && (
                  <span
                    className='input-group-text text-green-200'
                    id='search-component-word'
                  >
                    <i
                      className='fas fa-times click'
                      onClick={() => {
                        dispatch(ActionsFinancePatientTable.setSearchKey(''));
                      }}
                    />
                  </span>
                )}
              </div>
              <ExportButton
                params={{ pageNumber, pageSize, searchKey }}
                component='Patient'
              />
            </form>
          </div>
        </div>
      }
    >
      <div className='col-12 mb-3'>
        <div className='d-flex flex-wrap gap-3 p-3 border rounded bg-light'>
          <div className='d-flex align-items-center gap-2'>
            <span className='fw-bold'>Filters:</span>
          </div>
          <div className='d-flex align-items-center gap-2'>
            <span>Patient</span>
            <div style={{ minWidth: '200px' }}>
              <SelectPicker
                data={patientsSelectItems}
                value={selectedPatient}
                onChange={(value) => {
                  setSelectedPatient(value as string);
                  setSelectedCaregiver('');
                }}
                searchable
                cleanable
                placeholder='Select Patient'
                block
              />
            </div>
          </div>
          <div className='d-flex align-items-center gap-2'>
            <span>Caregiver</span>
            <div style={{ minWidth: '200px' }}>
              <SelectPicker
                data={caregiverSelectItems}
                value={selectedCaregiver}
                onChange={(value) => setSelectedCaregiver(value as string)}
                searchable
                cleanable
                placeholder={'Select Caregiver'}
                block
              />
            </div>
          </div>
        </div>
      </div>
      <PatientFinanceList
        data={financeData?.list}
        totalCount={financeData?.totalCount || 0}
        isValidating={isFetchingFinanceData}
        refetchFinanceData={refetchFinanceData}
      />
    </Card>
  );
};
